<template>
  <b-modal header-close-content="" id="general-options-modal" :title="$t('options')" ok-only data-cy="editor-general-option-modal">
    <b-tabs>
      <b-tab :title="$t('menubar.general')" class="p-2" data-cy="editor-general-options-tab">
        <div>
          <b-form-checkbox v-model="vm.settings.ShowConfirmationDialogOnDragAndDrop" inline data-cy="editor-drag-drop-confirm-cbx">
            {{ $t("menubar.options.showConfirmationOnDragDrop") }}
          </b-form-checkbox>
        </div>
        <div>
          <b-form-checkbox v-model="vm.settings.ShowConfirmationDialogIfAttributeGroupDiffers" inline data-cy="editor-ag-conflict-confirm-cbx">
            {{ $t("menubar.options.showAttributeGroupConflictMessage") }}
          </b-form-checkbox>
        </div>
      </b-tab>
      <b-tab :title="$t('menubar.liveedit')" class="p-2" data-cy="editor-live-edit-options-tab">
        <div>
          <b-form-checkbox v-model="vm.settings.IsLiveEditEnabled" inline data-cy="editor-live-edit-enable-cbx">
            {{ $t("menubar.options.isLiveEditEnabled") }}
          </b-form-checkbox>
        </div>
        <div>
          <b-form-checkbox v-model="vm.settings.IsShowPricesEnabled" inline data-cy="editor-live-edit-price-cbx">
            {{ $t("menubar.options.showLiveEditPrices") }}
          </b-form-checkbox>
        </div>
        <div>
          <b-form-checkbox v-model="vm.settings.IsLiveEditPaginationEnabled" inline data-cy="editor-live-edit-paging-cbx">
            {{ $t("menubar.options.enabledPagination") }}
          </b-form-checkbox>
        </div>
        <div class="pt-1 w-50">
          <strong class="pt-0"> {{ $t("menubar.options.articlePerPage") }}</strong>
          <b-dropdown class="dropdown-default align-self-center mt-2" :disabled="!vm.settings.IsLiveEditPaginationEnabled">
            <template #button-content>{{ $t("articleCount", { field: vm.settings.LiveEditArticlePagesize }) }}</template>
            <b-dropdown-item
              :key="articlePagesize.value"
              v-for="articlePagesize in articlePageSizes"
              @click="vm.settings.LiveEditArticlePagesize = articlePagesize.value">
              <span>{{ articlePagesize.text }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-tab>
      <b-tab :title="$t('menubar.tabs')" class="p-2 h-100" data-cy="editor-transltaion-options-tab">
        <div class="tabs-checkbox-container">
          <b-form-checkbox
            v-model="vm.settings.IsShowingCataloguesTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-catalogues-enable-cbx">
            {{ $t("menubar.options.showCatalogues") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="vm.settings.IsShowingClassificationTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-classification-enable-cbx">
            {{ $t("menubar.options.showClassification") }}
          </b-form-checkbox>

          <b-form-checkbox v-model="vm.settings.IsShowingERPTabEnabled" inline class="checkbox-item align-items-center" data-cy="editor-erp-enable-cbx">
            {{ $t("menubar.options.showERP") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="vm.settings.IsShowingGlobalAttributesTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-global-attributes-enable-cbx">
            {{ $t("menubar.options.showGlobalAttributes") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-if="checkForLicensed('GPSR')"
            v-model="vm.settings.IsShowingGPSRTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-gpsr-enable-cbx">
            {{ $t("menubar.options.showGPSR") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="vm.settings.IsShowingKeywordsTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-keywords-enable-cbx">
            {{ $t("menubar.options.showKeywords") }}
          </b-form-checkbox>

          <b-form-checkbox
            :disabled="vm.isJustOneTabEnabled"
            v-model="vm.settings.IsShowingMasterDataTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-masterdata-enable-cbx">
            {{ $t("menubar.options.showMasterData") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="vm.settings.IsShowingMediaAssetsTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-media-assets-enable-cbx">
            {{ $t("menubar.options.showMediaAssets") }}
          </b-form-checkbox>

          <b-form-checkbox v-model="vm.settings.IsShowingPricesTabEnabled" inline class="checkbox-item align-items-center" data-cy="editor-prices-enable-cbx">
            {{ $t("menubar.options.showPrices") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="vm.settings.IsShowingReferencesTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-references-enable-cbx">
            {{ $t("menubar.options.showReferences") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="vm.settings.IsShowingTableAttributeTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-table-attributes-enable-cbx">
            {{ $t("menubar.options.showTableAttributes") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-model="vm.settings.IsShowingTextAttributeTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-text-attributes-enable-cbx">
            {{ $t("menubar.options.showTextAttributes") }}
          </b-form-checkbox>

          <b-form-checkbox
            v-if="checkForLicensed('TranslationManager')"
            v-model="vm.settings.IsShowingTranslationTabEnabled"
            inline
            class="checkbox-item align-items-center"
            data-cy="editor-translation-enable-cbx">
            {{ $t("menubar.options.showTranslation") }}
          </b-form-checkbox>
        </div>
      </b-tab>

      <b-tab :title="$t('menubar.suggestions')" class="p-2" data-cy="editor-suggest-options-tab">
        <div class="section-wrapper">
          <div>
            <div class="d-flex align-items-center">
              <info-switch size="sm" v-model="suggestionsEnabled" :title="$t('AttributeSuggestions').toString()" data-cy="editor-suggest-enable-cbx" />
            </div>
            <div class="pl-3">
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  v-model="vm.settings.IsTextAttributeSuggestionsEnabled"
                  :disabled="!vm.intern_suggestionsEnabled"
                  inline
                  data-cy="editor-suggest-text-enable-cbx">
                  {{ $t("text.name") }}
                </b-form-checkbox>
              </div>
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  v-model="vm.settings.IsTableAttributeSuggestionsEnabled"
                  :disabled="!vm.intern_suggestionsEnabled"
                  inline
                  data-cy="editor-suggest-table-enable-cbx">
                  {{ $t("table.name") }}
                </b-form-checkbox>
              </div>
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  v-model="vm.settings.IsGlobalAttributeSuggestionsEnabled"
                  :disabled="!vm.intern_suggestionsEnabled"
                  inline
                  data-cy="editor-suggest-global-enable-cbx">
                  {{ $t("global.name") }}
                </b-form-checkbox>
              </div>
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  v-model="vm.settings.IsERPAttributeSuggestionsEnabled"
                  :disabled="!vm.intern_suggestionsEnabled"
                  inline
                  data-cy="editor-suggest-erp-enable-cbx">
                  {{ $t("erp.name") }}
                </b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<script setup lang="ts">
import InfoSwitch from "@common/components/InfoSwitch.vue";
import { useLicenses } from "@common/composable/useRootStore";
import { computed, onMounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useActions, useState } from "vuex-composition-helpers";

//#region General
const { t: $t } = useI18n();
type TabVisibilitySettings = {
  IsTextAttributeSuggestionsEnabled: boolean;
  IsTableAttributeSuggestionsEnabled: boolean;
  IsGlobalAttributeSuggestionsEnabled: boolean;
  IsERPAttributeSuggestionsEnabled: boolean;
  IsShowingCataloguesTabEnabled: boolean;
  IsShowingClassificationTabEnabled: boolean;
  IsShowingERPTabEnabled: boolean;
  IsShowingGPSRTabEnabled: boolean;
  IsShowingGlobalAttributesTabEnabled: boolean;
  IsShowingKeywordsTabEnabled: boolean;
  IsShowingMasterDataTabEnabled: boolean;
  IsShowingMediaAssetsTabEnabled: boolean;
  IsShowingPricesTabEnabled: boolean;
  IsShowingReferencesTabEnabled: boolean;
  IsShowingTableAttributeTabEnabled: boolean;
  IsShowingTextAttributeTabEnabled: boolean;
  IsShowingTranslationTabEnabled: boolean;
  IsLiveEditEnabled: boolean;
  IsShowPricesEnabled: boolean;
  IsLiveEditPaginationEnabled: boolean;
  LiveEditArticlePagesize: number;
  ShowConfirmationDialogOnDragAndDrop: boolean;
  ShowConfirmationDialogIfAttributeGroupDiffers: boolean;
};
//#endregion

//#region Store
const { userSettings } = useState(["userSettings"]);
const { saveUserSettings } = useActions(["saveUserSettings"]);
const { checkForLicensed } = useLicenses();
//#endregion

//#region ViewModel
type ViewModel = {
  checked: boolean;
  intern_suggestionsEnabled: boolean;
  isJustOneTabEnabled: boolean;
  settings: TabVisibilitySettings;
};
const vm = reactive<ViewModel>({
  checked: true,
  intern_suggestionsEnabled: true,
  isJustOneTabEnabled: false,
  settings: {
    IsTextAttributeSuggestionsEnabled: true,
    IsTableAttributeSuggestionsEnabled: true,
    IsGlobalAttributeSuggestionsEnabled: true,
    IsERPAttributeSuggestionsEnabled: true,
    IsShowingCataloguesTabEnabled: true,
    IsShowingClassificationTabEnabled: true,
    IsShowingERPTabEnabled: true,
    IsShowingGPSRTabEnabled: true,
    IsShowingGlobalAttributesTabEnabled: true,
    IsShowingKeywordsTabEnabled: true,
    IsShowingMasterDataTabEnabled: true,
    IsShowingMediaAssetsTabEnabled: true,
    IsShowingPricesTabEnabled: true,
    IsShowingReferencesTabEnabled: true,
    IsShowingTableAttributeTabEnabled: true,
    IsShowingTextAttributeTabEnabled: true,
    IsShowingTranslationTabEnabled: true,
    IsLiveEditEnabled: true,
    IsShowPricesEnabled: true,
    IsLiveEditPaginationEnabled: true,
    LiveEditArticlePagesize: 5,
    ShowConfirmationDialogOnDragAndDrop: true,
    ShowConfirmationDialogIfAttributeGroupDiffers: true
  }
});
//#endregion

//#region UserSettings
const ensureAtLeastOneTabEnabled = () => {
  const tabKeys: Array<keyof TabVisibilitySettings> = [
    "IsShowingCataloguesTabEnabled",
    "IsShowingClassificationTabEnabled",
    "IsShowingERPTabEnabled",
    "IsShowingGPSRTabEnabled",
    "IsShowingGlobalAttributesTabEnabled",
    "IsShowingKeywordsTabEnabled",
    "IsShowingMediaAssetsTabEnabled",
    "IsShowingPricesTabEnabled",
    "IsShowingReferencesTabEnabled",
    "IsShowingTableAttributeTabEnabled",
    "IsShowingTextAttributeTabEnabled",
    "IsShowingTranslationTabEnabled"
  ];

  const isAnyEnabled = tabKeys.some((key) => vm.settings[key]);

  if (!isAnyEnabled) {
    vm.isJustOneTabEnabled = true;
    vm.settings.IsShowingMasterDataTabEnabled = true;
  } else {
    vm.isJustOneTabEnabled = false;
  }
};

watch(
  () => vm.settings,
  (nv) => {
    checkForSuggestionEnabled(vm.settings);
    saveUserSettings(nv);
  },
  { deep: true }
);
watch(
  () => userSettings.value,
  (nv) => {
    if (!nv) return;

    Object.assign(vm.settings, {
      IsTextAttributeSuggestionsEnabled: nv.IsTextAttributeSuggestionsEnabled === true || nv.IsTextAttributeSuggestionsEnabled === "true",
      IsTableAttributeSuggestionsEnabled: nv.IsTableAttributeSuggestionsEnabled === true || nv.IsTableAttributeSuggestionsEnabled === "true",
      IsGlobalAttributeSuggestionsEnabled: nv.IsGlobalAttributeSuggestionsEnabled === true || nv.IsGlobalAttributeSuggestionsEnabled === "true",
      IsERPAttributeSuggestionsEnabled: nv.IsERPAttributeSuggestionsEnabled === true || nv.IsERPAttributeSuggestionsEnabled === "true",
      IsLiveEditEnabled: nv.IsLiveEditEnabled === true || nv.IsLiveEditEnabled === "true",
      IsShowPricesEnabled: nv.IsShowPricesEnabled === true || nv.IsShowPricesEnabled === "true",
      IsLiveEditPaginationEnabled: nv.IsLiveEditPaginationEnabled === true || nv.IsLiveEditPaginationEnabled === "true",
      LiveEditArticlePagesize: nv.LiveEditArticlePagesize,
      ShowConfirmationDialogOnDragAndDrop: nv.ShowConfirmationDialogOnDragAndDrop === true || nv.ShowConfirmationDialogOnDragAndDrop === "true",
      IsShowingCataloguesTabEnabled: nv.IsShowingCataloguesTabEnabled === true || nv.IsShowingCataloguesTabEnabled === "true",
      IsShowingClassificationTabEnabled: nv.IsShowingClassificationTabEnabled === true || nv.IsShowingClassificationTabEnabled === "true",
      IsShowingERPTabEnabled: nv.IsShowingERPTabEnabled === true || nv.IsShowingERPTabEnabled === "true",
      IsShowingGlobalAttributesTabEnabled: nv.IsShowingGlobalAttributesTabEnabled === true || nv.IsShowingGlobalAttributesTabEnabled === "true",
      IsShowingGPSRTabEnabled: nv.IsShowingGPSRTabEnabled === true || nv.IsShowingGPSRTabEnabled === "true",
      IsShowingKeywordsTabEnabled: nv.IsShowingKeywordsTabEnabled === true || nv.IsShowingKeywordsTabEnabled === "true",
      IsShowingMasterDataTabEnabled: nv.IsShowingMasterDataTabEnabled === true || nv.IsShowingMasterDataTabEnabled === "true",
      IsShowingMediaAssetsTabEnabled: nv.IsShowingMediaAssetsTabEnabled === true || nv.IsShowingMediaAssetsTabEnabled === "true",
      IsShowingPricesTabEnabled: nv.IsShowingPricesTabEnabled === true || nv.IsShowingPricesTabEnabled === "true",
      IsShowingReferencesTabEnabled: nv.IsShowingReferencesTabEnabled === true || nv.IsShowingReferencesTabEnabled === "true",
      IsShowingTableAttributeTabEnabled: nv.IsShowingTableAttributeTabEnabled === true || nv.IsShowingTableAttributeTabEnabled === "true",
      IsShowingTextAttributeTabEnabled: nv.IsShowingTextAttributeTabEnabled === true || nv.IsShowingTextAttributeTabEnabled === "true",
      IsShowingTranslationTabEnabled: nv.IsShowingTranslationTabEnabled === true || nv.IsShowingTranslationTabEnabled === "true",
      ShowConfirmationDialogIfAttributeGroupDiffers:
        nv.ShowConfirmationDialogIfAttributeGroupDiffers === true || nv.ShowConfirmationDialogIfAttributeGroupDiffers === "true"
    });
    ensureAtLeastOneTabEnabled();
  },
  { deep: true, immediate: true }
);
//#endregion

//#region Page/Suggestions
const suggestionsEnabled = computed({
  get: () => {
    return vm.intern_suggestionsEnabled;
  },
  set: (newValue) => {
    vm.intern_suggestionsEnabled = newValue;
    vm.settings.IsTextAttributeSuggestionsEnabled = newValue;
    vm.settings.IsTableAttributeSuggestionsEnabled = newValue;
    vm.settings.IsGlobalAttributeSuggestionsEnabled = newValue;
    vm.settings.IsERPAttributeSuggestionsEnabled = newValue;
  }
});

const checkForSuggestionEnabled = (settings: ViewModel["settings"]) => {
  if (
    settings.IsTextAttributeSuggestionsEnabled === false &&
    settings.IsTableAttributeSuggestionsEnabled === false &&
    settings.IsGlobalAttributeSuggestionsEnabled === false &&
    settings.IsERPAttributeSuggestionsEnabled === false
  ) {
    vm.intern_suggestionsEnabled = false;
  }
};

const articlePageSizes = computed(() => {
  const resultList = [];
  for (let i = 1; i <= 5; i++) {
    resultList.push({
      value: 5 * i,
      text: $t("articleCount", { field: 5 * i }).toString()
    });
  }
  return resultList;
});
//#endregion
onMounted(() => {
  checkForSuggestionEnabled(userSettings.value);
});
</script>
